import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BankingService } from '../../features/banking/services/banking.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { CurrencyAdjustmentService } from '../../features/currency-adjustment/services/currency-adjustment.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AccountTypesEnum } from '../../../assets/enum/accountTypesEnum';

@Component({
  selector: 'app-add-bank-account',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    FormsModule,
  ],
  templateUrl: './add-bank-account.component.html',
  styleUrl: './add-bank-account.component.scss',
})
export class AddBankAccountComponent {
  addBankAccountForm: FormGroup;
  search: string | undefined;
  filter: {};
  currencyData: any;
  bankDetail: any;
  accountsData: any;
  accountId: any;
  type: any;
  public bankAccountsData: any[] = [];

  constructor(
    private fb: FormBuilder,
    private currencyService: CurrencyAdjustmentService,
    private bankingService: BankingService,
    private toastr: ToastrService,
    private route: Router,
    private router: ActivatedRoute
  ) {}

  ngOnInit() {
    this.initForm();
    this.fetchCurrencies();
    this.loadAccountTypes();
    this.addBankAccountForm.get('bankType')?.valueChanges.subscribe((type) => {
      this.setConditionalValidators(type);
    });

    // Initial call in case of patching values or default selection
    const initialType = this.addBankAccountForm.get('bankType')?.value;
    this.setConditionalValidators(initialType);
    this.router.paramMap.subscribe((params) => {
      this.accountId = params.get('id');

      this.router.queryParamMap.subscribe((queryParams) => {
        this.type = queryParams.get('type');
        if (this.type === 'bank') {
          this.getBankAccountById(this.accountId);
        }
        if (this.type === 'credit') {
          this.getCreditCardById(this.accountId);
        }
      });
    });
  }

  initForm() {
    if (this.accountsData) {
      this.addBankAccountForm = this.fb.group({
        bankType: [this.type === 'credit' ? 'Credit' : 'Bank'],
        // account
        bankName: [this.accountsData?.bankName],
        accountNumber: [this.accountsData?.accountNumber],
        // IFSCCode: [this.accountsData?.IFSCCode],
        accountType: [this.accountsData?.accountType],
        currency: [this.accountsData?.currency],
        openingBalance: [this.accountsData?.openingBalance],
        // card
        cardName: [this.accountsData?.cardName],
        cardCurrency: [this.accountsData?.currency],
        cardNumberMasked: [this.accountsData?.cardNumberMasked],
        issuer: [this.accountsData?.issuer],
        creditLimit: [this.accountsData?.creditLimit],
        currentBalance: [this.accountsData?.currentBalance],
        expiryDate: [
          this.accountsData?.expiryDate
            ? new Date(+this.accountsData.expiryDate)
            : null,
        ],
      });
    } else {
      this.addBankAccountForm = this.fb.group({
        // account
        bankType: ['Bank'],
        bankName: [''],
        accountNumber: [''],
        // IFSCCode: [''],
        accountType: [''],
        currency: [''],
        openingBalance: [null],

        // card
        cardName: [''],
        cardNumberMasked: [''],
        issuer: [''],
        cardCurrency: [''],
        creditLimit: [null],
        currentBalance: [null],
        expiryDate: [''],
      });
    }
  }

  setConditionalValidators(type: string) {
    if (type === 'Bank') {
      // Bank fields required
      this.setRequired([
        'bankName',
        'accountNumber',
        'accountType',
        'currency',
        'openingBalance',
      ]);
      this.addBankAccountForm
        .get('accountNumber')
        ?.setValidators([
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9 ]+$/),
        ]);
      this.clearValidatorsAndReset([
        'cardName',
        'cardNumberMasked',
        'issuer',
        'cardCurrency',
        'creditLimit',
        'currentBalance',
        'expiryDate',
      ]);
    } else if (type === 'Credit') {
      // Credit Card fields required
      this.setRequired([
        'cardName',
        'cardNumberMasked',
        'issuer',
        'cardCurrency',
        'creditLimit',
        'currentBalance',
        'expiryDate',
      ]);
      this.addBankAccountForm.get('cardNumberMasked')?.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9 ]+$/), // allow only digits & spaces
        (control) => {
          if (!control.value) return null;
          const digitsOnly = control.value.replace(/\s+/g, ''); // remove spaces
          return digitsOnly.length >= 10 && digitsOnly.length <= 20
            ? null
            : { cardLength: true };
        },
      ]);

      this.clearValidatorsAndReset([
        'bankName',
        'accountNumber',
        'accountType',
        'currency',
        'openingBalance',
      ]);
    }

    this.addBankAccountForm.updateValueAndValidity();
  }

  setRequired(fields: string[]) {
    fields.forEach((field) => {
      this.addBankAccountForm.get(field)?.setValidators([Validators.required]);
      this.addBankAccountForm.get(field)?.updateValueAndValidity();
    });
  }

  clearValidatorsAndReset(fields: string[]) {
    fields.forEach((field) => {
      const control = this.addBankAccountForm.get(field);
      control?.clearValidators();
      control?.setValue(null);
      control?.markAsPristine();
      control?.markAsUntouched();
      control?.updateValueAndValidity();
    });
  }

  allowOnlyNumberWithDecimal(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow digits and decimal point
    if (!/^[0-9.]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Disallow more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  blockPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!/^\d*\.?\d*$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  private loadAccountTypes() {
    this.bankAccountsData = Object.values(AccountTypesEnum).map((type) => ({
      label: type.label,
      value: type.value,
    }));
  }

  fetchCurrencies() {
    this.currencyService.fetchCurrencies(this.search, this.filter).subscribe({
      next: (currency) => {
        this.currencyData = currency;
      },
      error: (error) => console.error(error),
    });
  }

  getBankAccountById(accountId: any) {
    this.bankingService.getBankAccountById(Number(accountId)).subscribe({
      next: (res) => {
        // console.log(res);
        this.accountsData = res;
        this.initForm();
        const currentType = this.addBankAccountForm.get('bankType')?.value;
        this.setConditionalValidators(currentType);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getCreditCardById(accountId: any) {
    this.bankingService.getCreditCardById(Number(accountId)).subscribe({
      next: (res) => {
        // console.log(res);
        this.accountsData = res;
        this.initForm();
        const currentType = this.addBankAccountForm.get('bankType')?.value;
        this.setConditionalValidators(currentType);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onSave() {
    if (this.addBankAccountForm.invalid) {
      this.addBankAccountForm.markAllAsTouched();
      return;
    }
    const formValue = this.addBankAccountForm.value;
    const branchId = localStorage.getItem('selectedBranchId');
    const createBankAccountInput = {
      ...(this.accountId && { id: Number(this.accountId) }),
      bankName: formValue.bankName,
      branchId: Number(branchId),
      accountNumber: formValue.accountNumber,
      accountType: formValue.accountType,
      currency: formValue.currency,
      openingBalance: parseFloat(formValue.openingBalance),
    };

    const createCreditCardInput = {
      ...(this.accountId && { id: Number(this.accountId) }),
      cardName: formValue.cardName,
      branchId: Number(branchId),
      cardNumberMasked: formValue.cardNumberMasked,
      issuer: formValue.issuer,
      creditLimit: parseFloat(formValue.creditLimit),
      currency: formValue.cardCurrency,
      currentBalance: parseFloat(formValue.currentBalance),
      expiryDate: formValue.expiryDate,
    };

    const bankType = formValue.bankType;
    console.log(bankType);

    if (bankType === 'Bank') {
      if (this.accountId) {
        this.bankingService
          .updateBank(createBankAccountInput, Number(this.accountId))
          .subscribe({
            next: (res) => {
              this.toastr.success('Bank account successfully updated');
              this.route.navigate(['/banking']);
            },
            error: (err) => {
              this.toastr.error(err?.message);
              console.log(err);
            },
          });
      } else {
        this.bankingService.createBank(createBankAccountInput).subscribe({
          next: (res) => {
            this.toastr.success('Bank account successfully added');
            this.route.navigate(['/banking']);
          },
          error: (err) => {
            this.toastr.error(err?.message);
            console.log(err);
          },
        });
      }
    } else {
      if (this.accountId) {
        this.bankingService
          .updateCreditCard(createCreditCardInput, Number(this.accountId))
          .subscribe({
            next: (res) => {
              this.toastr.success('Credit card successfully updated');
              this.route.navigate(['/banking']);
            },
            error: (err) => {
              this.toastr.error(err?.message);
              console.log(err);
            },
          });
      } else {
        this.bankingService.createCreditCard(createCreditCardInput).subscribe({
          next: (res) => {
            this.toastr.success('Credit card successfully added');
            this.route.navigate(['/banking']);
          },
          error: (err) => {
            this.toastr.error(err?.message);
            console.log(err);
          },
        });
      }
    }
  }

  get isCreditCard(): boolean {
    return this.addBankAccountForm.get('bankType')?.value === 'Credit';
  }

  onCancel() {
    this.route.navigate(['/banking']);
  }
}
